import { useMemo, useState } from "react";
import { categories, timeline, type GuaEvent } from "@/mocks/gossip";
import SectionHeading from "./SectionHeading";

function GuaCard({ item }: { item: GuaEvent }) {
  const [open, setOpen] = useState(false);

  return (
    <article className="w-full rounded-2xl bg-background-100 p-5 ring-1 ring-background-200 transition-colors hover:ring-primary-300 md:p-6">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h4 className="font-heading text-base font-bold text-primary-700 md:text-lg">
          {item.date} · {item.weekday}
        </h4>
        <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900">
          <i className="ri-folder-2-line"></i> {item.category}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-800">
          <i className="ri-flashlight-fill"></i> {item.tag}
        </span>
        <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary-600">
          <i className="ri-fire-fill"></i> 热度 {item.heat}
        </span>
      </div>

      <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 md:text-xl">
        {item.title}
      </h3>
      <p className="mt-3 text-sm leading-6 text-foreground-600 md:text-[15px] md:leading-7">{item.summary}</p>

      {open && (
        <ol className="mt-5 space-y-0 overflow-hidden rounded-xl border border-background-200 bg-background-50/70">
          {item.steps.map((s, i) => (
            <li key={`${item.id}-${i}`} className="relative flex gap-3 px-4 py-3">
              {i < item.steps.length - 1 && (
                <span className="absolute left-[19px] top-9 bottom-0 w-px bg-background-200"></span>
              )}
              <span className="z-10 mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-500 text-[10px] font-bold text-foreground-50">
                {i + 1}
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-accent-700">{s.time}</p>
                <p className="mt-1 text-sm leading-6 text-foreground-700">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-primary-600 transition-colors hover:text-primary-700"
      >
        {open ? (
          <>
            <i className="ri-subtract-line"></i> 收起经过节点
          </>
        ) : (
          <>
            <i className="ri-add-line"></i> 展开完整经过（{item.steps.length} 个节点）
          </>
        )}
      </button>
    </article>
  );
}

export default function TimelineSection() {
  const [activeCat, setActiveCat] = useState("全部");
  const [query, setQuery] = useState("");

  const list = useMemo(() => {
    const kw = query.trim();
    return timeline.filter((t) => {
      const inCat = activeCat === "全部" || t.category === activeCat;
      if (!inCat) return false;
      if (!kw) return true;
      const hay = `${t.title}${t.summary}${t.category}${t.tag}${t.date}`;
      return hay.includes(kw);
    });
  }, [activeCat, query]);

  const hasActive = activeCat !== "全部" || query.trim() !== "";

  return (
    <section id="timeline" className="bg-background-100 py-14 md:py-20">
      <div className="container-x">
        <SectionHeading
          eyebrow="This Month Timeline"
          title="本月大瓜时间线"
          desc="按发生顺序逐条还原本月大瓜的经过，从第一手信息到发酵、回应与收尾，脉络全程清晰。点击“展开完整经过”可查看每个事件的推进节点。"
        />

        {/* filter bar */}
        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="分类筛选">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActiveCat(c)}
                className={`inline-flex items-center whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  activeCat === c
                    ? "bg-primary-500 text-foreground-50"
                    : "bg-background-50 text-foreground-700 ring-1 ring-background-300 hover:text-primary-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-80">
            <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索大瓜标题 / 关键词"
              aria-label="搜索大瓜"
              className="w-full rounded-full border border-background-300 bg-background-50 py-2 pl-10 pr-9 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
            {query && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-primary-600"
              >
                <i className="ri-close-circle-fill text-lg"></i>
              </button>
            )}
          </div>
        </div>

        {/* result meta */}
        <div className="mt-5 flex items-center gap-2 text-sm text-foreground-500">
          <i className="ri-bar-chart-line text-accent-600"></i>
          当前筛选出 <strong className="font-semibold text-foreground-900">{list.length}</strong> 条大瓜脉络记录
          {hasActive && (
            <button
              type="button"
              onClick={() => {
                setActiveCat("全部");
                setQuery("");
              }}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800 hover:bg-secondary-200"
            >
              重置筛选 <i className="ri-refresh-line"></i>
            </button>
          )}
        </div>

        {/* timeline rail */}
        <div className="relative mt-8">
          <div className="absolute bottom-4 left-[23px] top-4 w-px bg-primary-200 md:left-[27px]"></div>
          {list.length > 0 ? (
            <ul className="space-y-5 md:space-y-6">
              {list.map((t) => (
                <li key={t.id} className="relative flex gap-4 md:gap-5">
                  <div className="relative z-10 flex h-12 w-12 shrink-0 flex-col items-center justify-center md:h-14 md:w-14">
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-full text-foreground-50 ring-4 ring-background-100 md:h-14 md:w-14 ${
                        t.isHot ? "bg-accent-500" : "bg-primary-500"
                      }`}
                    >
                      <i className={`${t.isHot ? "ri-fire-fill text-lg" : "ri-sparkling-2-fill text-lg"}`}></i>
                    </span>
                  </div>
                  <div className="min-w-0 flex-1 pb-1">
                    <GuaCard item={t} />
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-2xl bg-background-50 px-6 py-14 text-center ring-1 ring-background-200">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-inbox-2-line text-2xl"></i>
              </span>
              <p className="mt-4 font-medium text-foreground-800">没有找到匹配的大瓜记录</p>
              <p className="mt-1 text-sm text-foreground-500">换个关键词或清除筛选试试</p>
              <button
                type="button"
                onClick={() => {
                  setActiveCat("全部");
                  setQuery("");
                }}
                className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-foreground-50 hover:bg-primary-600"
              >
                查看全部 <i className="ri-refresh-line"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}